import React, { useState } from 'react';
import { ListItem, Checkbox, IconButton, Icon } from '@material-ui/core';
import { rowStyles } from './styles';
import clsx from 'clsx';
import { get, set, size } from 'global/lib/lodash';
import { getAvg, percentify, stopProp } from 'global/utils';
import moment from 'moment';
import { useScreen } from 'global/styles';

const getWidth = (props) => {
  const { cols, showCheckbox, mod } = props;
  const per = 100 / (size(cols) - 1);
  var subtract = showCheckbox ? 36 : 0;
  if (mod == 'thin') {
    subtract = subtract + 28;
  } else if (mod == 'wide') {
    subtract = subtract - 84;
  }
  return `calc(${per}% - ${subtract}px)`;
};

const getValueForCompletion = (r, show0 = true) => {
  const val = r._userAssignments ? getAvg(r._userAssignments.filter((ua) => ua.completion != null).map((ua) => ua.completion)) : r.completion;
  if (val === null || val < 0 || isNaN(val)) {
    return show0 ? percentify(0) : 'Not Started';
  } else {
    return percentify(val || 0);
  }
};

const getValueForScore = (r, defaultToNa = false) => {
  const val = r._userAssignments ? getAvg(r._userAssignments.filter((ua) => ua.score != null).map((ua) => ua.score)) : r.score;

  const scorecardId = r.scorecardId !== undefined ? r.scorecardId : get(r, 'assignments', []).find((f) => f.scorecardId !== null);

  if (val === null || isNaN(val) || val < 0) {
    return scorecardId === null || scorecardId === undefined ? 'N/A' : defaultToNa ? 'N/A' : 'Not Started';
  } else if (val < 0) {
    return defaultToNa ? 'N/A' : 'Not Started';
  } else {
    return percentify(val || 0);
  }
};

const getCols = (filter, opts = {}) => {
  if (filter.report_type === 'user') {
    return {
      showCheckbox: true,
      cols: [
        { title: 'Name', val: (r) => get(r, 'reportable.name') },
        {
          title: 'Course Assignments',
          val: (r) => size(r._userAssignments || r.userAssignments),
          className: 'centerCold',
          dropKey: 'userAssignments',
          getName: (r) => get(r, 'editionName'),
          showDrop: true,
        },
        { title: 'Completion', val: (r) => getValueForCompletion(r, false), dropKey: 'userAssignments', getName: (r) => getValueForCompletion(r, false) },
        !opts.isMobile && { title: 'Quiz Scores', val: (r) => getValueForScore({ ...r, assignments: r._userAssignments || r.userAssignments }), dropKey: 'userAssignments', getName: getValueForScore },
        !opts.isMobile && { title: 'Last Updated', val: (r) => moment(r.updatedAt).fromNow(), getName: (r) => moment(r.updatedAt).fromNow(), dropKey: 'userAssignments' },
      ].filter((f) => !!f),
    };
  } else if (filter.report_type === 'edition') {
    return {
      showCheckbox: false,
      cols: [
        { title: 'Name', val: (r) => [`${get(r, 'reportable.name')}`, `${get(r, 'realm.name')}`], rowClass: 'link' },
        { title: 'Course Assignments', val: (r) => r.count, className: 'centerCold', dropKey: 'editionAssignments' },
        { title: 'Completion', val: getValueForCompletion },
        !opts.isMobile && { title: 'Quiz Scores', val: (r) => getValueForScore({ ...r, assignments: r.editionAssignments }) },
        !opts.isMobile && { title: 'Last Updated', val: (r) => moment(r.updatedAt).fromNow() },
      ].filter((f) => !!f),
    };
  } else if (filter.report_type === 'realm') {
    return {
      showCheckbox: false,
      cols: [
        { title: 'Name', val: (r) => [`${get(r, 'reportable.name')}`, `${get(r, 'realm.name')}`], rowClass: 'link' },
        { title: 'Course Assignments', val: (r) => r.count, className: 'centerCold' },
        { title: 'Completion', val: getValueForCompletion },
        !opts.isMobile && { title: 'Quiz Scores', val: (r) => getValueForScore({ ...r, assignments: r.children }, true) },
        !opts.isMobile && { title: 'Last Updated', val: (r) => moment(r.updatedAt).fromNow() },
      ].filter((f) => !!f),
    };
  } else {
    return { cols: [] };
  }
};

export const RowHeader = (props) => {
  const { filter } = props;
  const classes = rowStyles();
  const { isMobile } = useScreen();

  const COLS = getCols(filter, { isMobile });

  const width = getWidth(COLS);
  const wideWidth = getWidth({ ...COLS, mod: 'wide' });
  const thinWidth = getWidth({ ...COLS, mod: 'thin' });

  return (
    <ListItem className={clsx(classes.row, classes.headerRow)}>
      {COLS.showCheckbox && (
        <div className={classes.checkbox}>
          <Checkbox indeterminate={props.indeterminate} onClick={props.selectAll} checked={props.allSelected} />
        </div>
      )}
      {COLS.cols.map((c) => {
        const cWidth = c.title == 'Course Assignments' ? { width: wideWidth } : c.title == 'Completion' || c.title == 'Quiz Scores' || c.title == 'Last Updated' ? { width: thinWidth } : { width };
        return (
          <div key={c.title} style={cWidth} className={clsx(classes.col, classes[c.className])}>
            {c.title}
          </div>
        );
      })}
    </ListItem>
  );
};

export default (props) => {
  const classes = rowStyles();
  const { item, index, selected, filter } = props;
  const [open, setOpen] = useState(false);
  const { isMobile } = useScreen();

  const COLS = getCols(filter, { isMobile });

  const isSelected = () => {
    return get(selected, item.id) ? true : false;
  };

  const onSelectItem = (e) => {
    stopProp(e);
    props.onSelectItem && props.onSelectItem(props);
  };

  const onSelectRow = (e) => {
    props.onSelectRow && props.onSelectRow(e, props);
  };

  const toggleOpen = (e) => {
    stopProp(e);
    setOpen(!open);
  };

  const showbg = index % 2;

  const width = getWidth(COLS);
  const wideWidth = getWidth({ ...COLS, mod: 'wide' });
  const thinWidth = getWidth({ ...COLS, mod: 'thin' });

  if (item.userAssignments && props.filtChips) {
    const edFilt = props.filtChips.find((f) => f.filter === 'edition_id');
    if (edFilt) {
      set(
        item,
        '_userAssignments',
        item.userAssignments.filter((u) => u.editionId == get(edFilt, 'value'))
      );
    }
  }

  return (
    <ListItem onClick={onSelectRow} className={clsx(classes.row, classes.flexRow, classes.listRow, showbg && classes.rowBg)}>
      {COLS.showCheckbox && (
        <div className={classes.checkbox}>
          <Checkbox onClick={onSelectItem} checked={isSelected()} />
        </div>
      )}
      {COLS.cols.map((c, i) => {
        const value = c.val(item);
        const dropItems = get(item, `_${c.dropKey}`, get(item, c.dropKey, [])) || [];
        const cWidth = c.title == 'Course Assignments' ? { width: wideWidth } : c.title == 'Completion' || c.title == 'Quiz Scores' || c.title == 'Last Updated' ? { width: thinWidth } : { width };
        return (
          <div className={clsx(classes.col, classes[c.className], classes[c.rowClass])} style={cWidth}>
            <div className={classes.colText}>
              {open && dropItems.length > 1 ? (
                dropItems.map((r) => <div key={r.id}>{c.getName(r)}</div>)
              ) : c.showDrop && dropItems.length == 1 ? (
                <div>{c.getName(dropItems[0])}</div>
              ) : Array.isArray(value) ? (
                value.map((v, i) =>
                  i > 0 ? (
                    <div>
                      <em>{v}</em>
                    </div>
                  ) : (
                    <div>{v}</div>
                  )
                )
              ) : (
                <span>{value}</span>
              )}
            </div>
            {c.showDrop && dropItems.length > 1 && (
              <IconButton disabled={!size(get(item, c.dropKey, []))} onClick={toggleOpen} color="primary" component="span">
                <Icon>{open ? 'arrow_drop_up' : 'arrow_drop_down'}</Icon>
              </IconButton>
            )}
          </div>
        );
      })}
    </ListItem>
  );
};
